import { createFileRoute } from "@tanstack/react-router";

import styles from "@/styles/App.module.scss";
import { Outlet } from "@tanstack/react-router";
import Sidebar from "@/components/app/Sidebar";
import Snackbar from "@/components/app/Snackbar";
import { Suspense, useState } from "react";
import ErrorBoundary from "@/components/app/ErrorBoundaryInsideRouter";
import ConfirmProvider from "@/providers/ConfirmProvider";
import welcomeLogo from "@/assets/whiteLogo.svg";
import welcomeBlackLogo from "@/assets/blackLogo.svg";
import { useLoggedInUser, useReloadApp } from "@/store/zustandStore";
import { LoadingSpinner } from "@app-components";
import { useWelcomeFoodAccess } from "@/hooks/useWelcomeFoodAccess";

import AuthProvider from "@/providers/AuthProvider.tsx";

import BeamerProvider from "@/providers/Beamer.tsx";
import BuildingUserProvider from "@/providers/BuildingUserProvider.tsx";
import BottomTabBar from "@/components/dashboard/BottomTabBar";
import PortalLoadingOverlay from "@/components/PortalLoadingOverlay";
import {
  FoodNotifications,
  Notifications,
} from "@/components/notifications/Notifications";
import { useBuilding } from "@/api";
import { useTranslation } from "react-i18next";

const JustSidebarLoading = () => {
  return (
    <nav
      className={`${styles.sidebar} ${styles.sidebarLoading} ${styles.open}`}
    >
      <div className={`${styles.workspaceContainer} ${styles.open}`}>
        <img width={50} src={welcomeLogo} alt="Welcome Workdays logo" />

        <div
          className={`tooltip--LOGOUT ${styles.workspaceButton} ${styles.purpleOnHover}`}
        ></div>
      </div>

      <div className={`${styles.linksContainer} ${styles.open}`}>
        <div className={styles.sidebarLoadingOverlay}></div>
      </div>
    </nav>
  );
};

const FoodNotificationWrapper = () => {
  const { allowFoodUserAccess } = useWelcomeFoodAccess(true);
  if (!allowFoodUserAccess) return null;

  return <FoodNotifications />;
};

const SnackbarWrapper = () => {
  const user = useLoggedInUser();
  return <Snackbar completeLeftAlign={!user?.userActive} />;
};

const RedirectHandler = () => {
  const { data: building } = useBuilding();
  const { t } = useTranslation();
  if (!building.redirectToApp) return null;
  return (
    <div
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "white",
        zIndex: 9999,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <img width={100} src={welcomeBlackLogo} alt="Welcome Workdays logo" />
      <h1 style={{ marginBottom: "20px" }}>{t("REDIRECT.HEADER")}</h1>
      <h2>{t("REDIRECT.SUB_HEADER")}</h2>
      <a style={{ fontSize: "20px" }} href="https://app.welcomeworkdays.com">
        app.welcomeworkdays.com
      </a>
    </div>
  );
};

const PortalRoute = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { outletKey } = useReloadApp();

  return (
    <>
      <PortalLoadingOverlay />
      <div className={styles.mainLayout}>
        <Suspense fallback={<JustSidebarLoading />}>
          <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
        </Suspense>
        <main
          id="page-scroll-container"
          className={`${styles.mainContent} ${!sidebarOpen && styles.open}`}
          onClick={() => setSidebarOpen(false)}
          onTouchMove={() => setSidebarOpen(false)}
        >
          <ErrorBoundary insideRouter>
            <ConfirmProvider>
              <Suspense
                fallback={
                  <div className="center">
                    <LoadingSpinner />
                  </div>
                }
              >
                <Outlet key={outletKey} />
              </Suspense>
              <Suspense>
                <BottomTabBar />
              </Suspense>
              <Suspense>
                <Notifications />
                <FoodNotificationWrapper />
              </Suspense>
            </ConfirmProvider>
          </ErrorBoundary>
        </main>
      </div>
      <Suspense>
        <SnackbarWrapper />
      </Suspense>
      <Suspense>
        <RedirectHandler />
      </Suspense>
    </>
  );
};

export const Route = createFileRoute("/_portal")({
  component: () => {
    return (
      <AuthProvider>
        <BuildingUserProvider>
          <BeamerProvider>
            <PortalRoute />
          </BeamerProvider>
        </BuildingUserProvider>
      </AuthProvider>
    );
  },
});
